import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { BasicProviderProps, IssueType, IssueWorkflow, PinnedDashboard, TicketsOpModuleContextType, WorkflowStatus } from '../../../types'
import ErrorBoundary from '../../../components/error/ErrorBoundary'
import useDataClient from '../../../hooks/useDataClient';
import { AppContext } from '../../../context/AppContext';
import { DashboardContext } from '../../../context/DashboardContext';
import { ticketsAdapter } from '../../../adapter/tickets.adapter';

interface TicketsOperationProviderProps extends BasicProviderProps {
    path: string,
    dashboardId?: string
}

export const TicketsOpModuleContext = createContext<TicketsOpModuleContextType | null>(null);

export default function TicketsOperationProvider({ path, dashboardId, children }: Readonly<TicketsOperationProviderProps>) {
    const appContext = useContext(AppContext);
    const dashboardContext = useContext(DashboardContext);
    const [showForm, setShowForm] = useState(false);
    const ticketWorkflowClient = useDataClient<IssueWorkflow>({ appContext });
    const sendIssueClient = useDataClient<IssueWorkflow>({ appContext });
    const target = useMemo<PinnedDashboard | null>(() => {
        let data = dashboardContext?.appData.adminTickets?.reduce((prevValue: PinnedDashboard[], current) => [...prevValue, ...current.dashboards], []) || [];
        return data.find(d => d.id === Number(dashboardId) || d.dashboardId === dashboardId) || null;
    }, [dashboardId]);
    const workflow = useMemo<WorkflowStatus[]>(() => sendIssueClient.status.data?.status || ticketWorkflowClient.status.data?.status || [], [ticketWorkflowClient.status])

    function sendIssue(issue: IssueType, files: File[]) {
        sendIssueClient.executeRequest(ticketsAdapter.sendIssue(issue, files));
    }

    const contextData = useMemo<TicketsOpModuleContextType>(() => ({
        workflow,
        path,
        showForm,
        setShowForm,
        platforms: dashboardContext?.appData.catalogGeneric?.ticketModuloOp || [],
        process: dashboardContext?.appData.catalogGeneric?.ticketProcesoOp || [],
        sendIssue,
        target
    }), [workflow, showForm, target]);

    useEffect(() => {
        if (target?.ticketType) {
            sendIssueClient.clean()
            ticketWorkflowClient.executeRequest(ticketsAdapter.listIssues(target));
        }
    }, [target]);


    useEffect(() => {
        if (workflow.length > 0) {
            setShowForm(false);
        }
    }, [workflow]);

    return <ErrorBoundary>
        <TicketsOpModuleContext.Provider value={contextData}>
            {children}
        </TicketsOpModuleContext.Provider>
    </ErrorBoundary>
}
